"use client";

import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion } from "framer-motion";
import {
  useHydratedScroll,
  useHydratedParallax,
} from "@/hooks/useHydratedScroll";
import { getImageUrl } from "@/utils/utils";
import { ChevronRight } from "lucide-react";

interface HomePageCareerClientProps {
  lang: string;
  homepageContent: any;
}

export default function HomePageCareerClient({
  lang,
  homepageContent,
}: HomePageCareerClientProps) {
  const [isMobile, setIsMobile] = useState(false);
  const careerRef = useRef<HTMLElement>(null);

  // State to track if component is mounted (prevents hydration errors)
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Hydration-safe scroll hooks
  const { scrollYProgress: careerScrollProgress, isMounted: careerMounted } =
    useHydratedScroll({
      target: careerRef,
      offset: ["-200px end", "end -200px"],
    });

  const careerBackgroundY = useHydratedParallax(
    careerScrollProgress,
    ["-30%", "30%"],
    careerMounted
  );

  // Mobile detection for optimized animations
  useEffect(() => {
    const checkDevice = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkDevice();
    window.addEventListener("resize", checkDevice);

    return () => window.removeEventListener("resize", checkDevice);
  }, []);

  // Animation variants - exact match from original
  const sectionVariants = {
    hidden: {
      opacity: 0,
      y: isMobile ? 30 : 60, // Reduced movement on mobile
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: isMobile ? 0.6 : 0.8, // Faster animation on mobile
        ease: "easeOut" as const,
      },
    },
  };

  return (
    <motion.section
      ref={careerRef}
      className="relative py-[100px] text-white overflow-hidden"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      variants={sectionVariants}
    >
      {/* Parallax Background */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute w-full h-[120%] -top-[10%] sm:h-[180%] sm:-top-[40%] lg:h-[130%] lg:-top-[15%]"
          style={{
            y: careerMounted ? careerBackgroundY : 0,
          }}
        >
          {homepageContent?.career?.backgroundImage && (
            <Image
              src={getImageUrl(
                homepageContent?.career?.backgroundImage?.url || ""
              )}
              alt={
                homepageContent?.career?.backgroundImage?.name ||
                "Career Background"
              }
              fill
              className="object-cover object-center"
              priority
            />
          )}
        </motion.div>
        {/* Overlay for text readability - positioned between image and text */}
        <div className="absolute inset-0 opacity-80 bg-[#1A1D21] z-10"></div>
      </div>

      <div className="relative z-20 max-w-4xl mx-auto px-4 lg:px-6 text-center">
        <div className="flex items-center justify-center gap-4 mb-4">
          <div className="w-8 h-px bg-white/50"></div>
          <span className="text-sm capitalize tracking-wide text-white/80">
            {homepageContent?.career?.title || "Career"}
          </span>
          <div className="w-8 h-px bg-white/50"></div>
        </div>
        <h2 className="text-[32px] sm:text-[40px] lg:text-[48px] leading-[38px] sm:leading-[48px] lg:leading-[58px] font-medium text-white mb-[16px] sm:mb-[20px] lg:mb-[23px]">
          {homepageContent?.career?.subtitle || "Join Our Team"}
        </h2>
        <p className="text-[16px] sm:text-[18px] leading-[24px] sm:leading-[28px] text-white mb-[30px] sm:mb-[40px] max-w-2xl mx-auto">
          {homepageContent?.career?.description || ""}
        </p>
        <Link
          href={`/${lang}/career`}
          className="group inline-flex items-center gap-2 px-[24px] py-[17px] text-base sm:text-lg rounded-[30px] bg-[#1A5852] text-white hover:bg-white hover:text-[#1A5852] transition-all duration-300 cursor-pointer"
        >
          <ChevronRight
            size={16}
            className="transition-colors duration-300"
            color="currentColor"
          />
          {homepageContent?.career?.ctaText || "View Open Positions"}
        </Link>
      </div>
    </motion.section>
  );
}
